'use client';

import { useState } from 'react';
import { CollapsibleHeader } from './CollapsibleHeader';
import Checkbox from '@/components/subflow-v3/components/Checkbox/Checkbox';
import { trackElementPresented } from '@/utils/rudderstack';
import classNames from 'classnames';
import styles from './FilterSection.module.scss';

export interface FilterOption {
  label: string;
  value: string;
}

export interface FilterSectionProps {
  title: string;
  options: FilterOption[];
  selectedValues: string[];
  onSelectionChange: (values: string[]) => void;
  isInitiallyOpen?: boolean;
  allowMultiple?: boolean;
}

export const FilterSection = ({
  title,
  options,
  selectedValues,
  onSelectionChange,
  isInitiallyOpen = false,
  allowMultiple = true,
}: FilterSectionProps) => {
  const [isOpen, setIsOpen] = useState(isInitiallyOpen);

  const handleToggle = () => {
    if (!isOpen) {
      trackElementPresented({
        name: 'Filter Accordion',
        title,
      });
    }
    setIsOpen(!isOpen);
  };

  const handleCheckboxChange = (value: string, checked: boolean) => {
    if (allowMultiple) {
      const newValues = checked
        ? [...selectedValues, value]
        : selectedValues.filter((v) => v !== value);
      onSelectionChange(newValues);
    } else {
      // Single selection mode
      const newValues = checked ? [value] : [];
      onSelectionChange(newValues);
    }
  };

  return (
    <div className="bg-white w-full" data-testid={`filter-section-${title}`}>
      <CollapsibleHeader
        title={title}
        isOpen={isOpen}
        onToggle={handleToggle}
      />

      {isOpen && (
        <div className="pt-4">
          {/* Options list */}
          <div className={classNames('space-y-2', styles.filterList)}>
            {options.map((option) => (
              <Checkbox
                key={option.value}
                id={option.value}
                label={option.label}
                onChange={(value) =>
                  handleCheckboxChange(option.value, value.target.checked)
                }
                value={selectedValues.includes(option.value)}
                isLabelClickable={true}
                data-testid={`filter-option-${option.value}`}
                checkboxLabelClass="!leading-[18px] !relative"
                className="flex items-start"
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
